import { z } from 'zod/v4';

export const ListOrderParticipantsBodySchema = z.object({
  /** Purchase-order ids — at least one, none empty, no repeats. */
  orderIds: z
    .array(z.string().min(1))
    .min(1)
    .refine(ids => new Set(ids).size === ids.length, {
      message: 'orderIds must not repeat',
    }),
  /**
   * `true`: only participants the caller has in its network (a connection
   * type is set); `false`, the default: only those it has not.
   */
  hasParticipantConnection: z.boolean().default(false),
});

export type ListOrderParticipantsBody = z.input<
  typeof ListOrderParticipantsBodySchema
>;

/** A company on an order's trace other than the caller. */
export const OrderParticipantSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  country: z.string().nullable(),
  /** Comma-separated facility-process ids, e.g. `"ASSEMBLY,CUT_MAKE_TRIM"`. */
  facilityProcesses: z.string().nullable(),
  featuredImageFileUrl: z.string().nullable(),
  /** The caller's connection to it (`SUPPLIER`, `CLIENT`, …); `null` when not in its network. */
  connectionType: z.string().nullable(),
  /** The relationship type's name in the caller's profile of the company. */
  relationshipType: z.string().nullable(),
  /** The collaboration status's name in the caller's profile of the company. */
  collaborationStatus: z.string().nullable(),
});

export type OrderParticipant = z.infer<typeof OrderParticipantSchema>;

/** Each participant once, in the order the trace first reaches it. */
export const OrderParticipantListSchema = z.array(OrderParticipantSchema);

/** Wire shape: the standard `reply.apiSuccess` envelope around the list. */
export const ListOrderParticipantsResponseSchema = z.object({
  metadata: z.object({ success: z.literal(true) }),
  data: OrderParticipantListSchema,
});

export type ListOrderParticipantsResponse = z.infer<
  typeof ListOrderParticipantsResponseSchema
>;

export const ORDER_PARTICIPANTS_ERROR_CODES = {
  INVALID_ORDER_ID: 'invalid_order_id',
} as const;
